/**
 * Gobernanza de la memoria (seccion 11).
 *
 * Una memoria que se escribe sola y no se puede mirar es una caja negra. Lo
 * que el agente recuerda tiene que poder verse, corregirse y borrarse.
 */
import { queryOptions } from "@tanstack/react-query";

import { request } from "../lib/api-client.ts";
import { API_BASE } from "../lib/api-routes.ts";
import type { Schemas } from "../lib/api-schemas.ts";

/**
 * Los alcances que pueden llegar en una respuesta: `user` y `client`.
 *
 * El porqué de que no haya un tercero lo explica el propio contrato, en la
 * descripción del campo generado; no se repite aquí para que no acaben
 * diciendo cosas distintas. Lo que importa de este lado: cuando el backend
 * añada uno, aparecerá al regenerar y el compilador señalará los sitios que
 * no lo contemplan --empezando por las etiquetas de `MemoryPanel`--.
 */
export type MemoryScope = Schemas["MemoryIndexEntry"]["scopeType"];

export type MemoryEntry = Readonly<Schemas["MemoryIndexEntry"]>;
export type MemoryNote = Readonly<Schemas["Note"]>;
export type MemoryIndex = Readonly<Schemas["MemoryIndex"]>;

export const listMemory = (): Promise<MemoryIndex> =>
  request<MemoryIndex>(`${API_BASE}/memory`);

export const readMemoryNote = (slug: string): Promise<MemoryNote> =>
  request<MemoryNote>(`${API_BASE}/memory/${encodeURIComponent(slug)}`);

export const saveMemoryNote = (
  slug: string,
  body: { summary: string; content: string; scope: MemoryScope },
): Promise<unknown> =>
  request(`${API_BASE}/memory/${encodeURIComponent(slug)}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });

export const deleteMemoryNote = (
  slug: string,
  scope: MemoryScope,
): Promise<unknown> =>
  request(`${API_BASE}/memory/${encodeURIComponent(slug)}?scope=${scope}`, {
    method: "DELETE",
  });

/** El ÍNDICE de la memoria: una línea por nota, igual que lo ve el agente. */
export const memoryQuery = () =>
  queryOptions({
    queryKey: ["memory"] as const,
    queryFn: listMemory,
  });
